import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { cn } from "../utils/cn";

// ─── Types ───────────────────────────────────────────────────────────────────

export type ToastType = "success" | "error" | "warning" | "info";
export type ToastPosition =
  | "top-right"
  | "top-left"
  | "top-center"
  | "bottom-right"
  | "bottom-left"
  | "bottom-center";

export interface ToastItem {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  dismissible?: boolean;
  action?: { label: string; onClick: () => void };
}

export interface ToastOptions {
  title: string;
  message?: string;
  duration?: number;
  dismissible?: boolean;
  action?: { label: string; onClick: () => void };
}

// ─── Context ─────────────────────────────────────────────────────────────────

export interface ToastAPI {
  success: (options: ToastOptions | string) => string;
  error: (options: ToastOptions | string) => string;
  warning: (options: ToastOptions | string) => string;
  info: (options: ToastOptions | string) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

interface ToastContextValue {
  toasts: ToastItem[];
  toast: ToastAPI;
}

const ToastContext = createContext<ToastContextValue | null>(null);

function normalizeOptions(options: ToastOptions | string): ToastOptions {
  if (typeof options === "string") return { title: options };
  return options;
}

// ─── Provider ────────────────────────────────────────────────────────────────

export interface ToastProviderProps {
  children: React.ReactNode;
  position?: ToastPosition;
  defaultDuration?: number;
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = "top-right",
  defaultDuration = 4000,
  maxToasts = 5,
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const counterRef = useRef(0);

  const addToast = useCallback(
    (type: ToastType, options: ToastOptions | string): string => {
      const normalized = normalizeOptions(options);
      const id = `toast-${++counterRef.current}-${Date.now()}`;
      const newToast: ToastItem = {
        id,
        type,
        duration: defaultDuration,
        dismissible: true,
        ...normalized,
      };
      setToasts((prev) => [newToast, ...prev].slice(0, maxToasts));
      return id;
    },
    [defaultDuration, maxToasts],
  );

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismissAll = useCallback(() => setToasts([]), []);

  const toast: ToastAPI = {
    success: (opts) => addToast("success", opts),
    error: (opts) => addToast("error", opts),
    warning: (opts) => addToast("warning", opts),
    info: (opts) => addToast("info", opts),
    dismiss,
    dismissAll,
  };

  return (
    <ToastContext.Provider value={{ toasts, toast }}>
      {children}
      <ToastContainer toasts={toasts} position={position} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

// ─── Hook ────────────────────────────────────────────────────────────────────

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastAPI {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx.toast;
}

// ─── Position Classes ─────────────────────────────────────────────────────────

const positionClasses: { [key in ToastPosition]: string } = {
  "top-right": "top-4 right-4 items-end",
  "top-left": "top-4 left-4 items-start",
  "top-center": "top-4 left-1/2 -translate-x-1/2 items-center",
  "bottom-right": "bottom-4 right-4 items-end",
  "bottom-left": "bottom-4 left-4 items-start",
  "bottom-center": "bottom-4 left-1/2 -translate-x-1/2 items-center",
};

// ─── Toast Style Config ───────────────────────────────────────────────────────
// NOTE: No JSX here — icons are rendered in ToastCard via a function
// This avoids TSX parser confusion with Record<Type, {ReactNode}>

interface ToastStyle {
  border: string;
  iconColor: string;
  progressColor: string;
}

const toastStyles: { [key in ToastType]: ToastStyle } = {
  success: {
    border: "border-l-green-500",
    iconColor: "text-green-500",
    progressColor: "bg-green-500",
  },
  error: {
    border: "border-l-red-500",
    iconColor: "text-red-500",
    progressColor: "bg-red-500",
  },
  warning: {
    border: "border-l-yellow-500",
    iconColor: "text-yellow-500",
    progressColor: "bg-yellow-500",
  },
  info: {
    border: "border-l-blue-500",
    iconColor: "text-blue-500",
    progressColor: "bg-blue-500",
  },
};

// ─── Toast Icon (JSX separated from config object) ────────────────────────────

function ToastIcon({ type }: { type: ToastType }): React.ReactElement {
  if (type === "success") {
    return (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    );
  }
  if (type === "error") {
    return (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    );
  }
  if (type === "warning") {
    return (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
    );
  }
  return (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        clipRule="evenodd"
      />
    </svg>
  );
}

// ─── Toast Card ───────────────────────────────────────────────────────────────

interface ToastCardProps {
  toast: ToastItem;
  onDismiss: (id: string) => void;
}

const ToastCard: React.FC<ToastCardProps> = ({ toast, onDismiss }) => {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  const style = toastStyles[toast.type];
  const duration = toast.duration ?? 4000;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    setTimeout(() => onDismiss(toast.id), 300);
  }, [toast.id, onDismiss]);

  useEffect(() => {
    if (duration === Infinity) return;
    const step = 50;
    const decrement = (step / duration) * 100;
    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) {
          clearInterval(intervalRef.current!);
          handleDismiss();
          return 0;
        }
        return prev - decrement;
      });
    }, step);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [duration, handleDismiss]);

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      className={cn(
        "relative overflow-hidden w-80 max-w-[calc(100vw-2rem)]",
        "rounded-xl border border-gray-100 border-l-4 shadow-lg bg-white",
        style.border,
        "transition-all duration-300 ease-out",
        visible
          ? "opacity-100 translate-y-0 scale-100"
          : "opacity-0 translate-y-2 scale-95",
      )}
    >
      <div className="flex items-start gap-3 p-4">
        <span
          className={cn("shrink-0 mt-0.5", style.iconColor)}
          aria-hidden="true"
        >
          <ToastIcon type={toast.type} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">{toast.title}</p>
          {toast.message && (
            <p className="mt-0.5 text-xs text-gray-500 leading-relaxed">
              {toast.message}
            </p>
          )}
          {toast.action && (
            <button
              type="button"
              onClick={() => {
                toast.action!.onClick();
                handleDismiss();
              }}
              className={cn(
                "mt-2 text-xs font-semibold underline-offset-2 hover:underline",
                style.iconColor,
              )}
            >
              {toast.action.label}
            </button>
          )}
        </div>
        {toast.dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss notification"
            className="shrink-0 rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg
              className="h-4 w-4"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>
      {duration !== Infinity && (
        <div className="h-0.5 bg-gray-100">
          <div
            className={cn("h-full transition-none", style.progressColor)}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

// ─── Container ────────────────────────────────────────────────────────────────

interface ToastContainerProps {
  toasts: ToastItem[];
  position: ToastPosition;
  onDismiss: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  position,
  onDismiss,
}) => {
  if (toasts.length === 0) return null;
  return (
    <div
      aria-label="Notifications"
      className={cn(
        "fixed z-[9999] flex flex-col gap-2 pointer-events-none",
        positionClasses[position],
      )}
    >
      {toasts.map((t) => (
        <div key={t.id} className="pointer-events-auto">
          <ToastCard toast={t} onDismiss={onDismiss} />
        </div>
      ))}
    </div>
  );
};

// Day 9 complete
